// src/pages/CalendarCallback.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { calendarService } from '../services/calendarService';
import posthog from 'posthog-js';
import { Calendar, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import styles from './CalendarCallback.module.css';

export default function CalendarCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Connecting your calendar...');

  useEffect(() => {
    if (user) {
      handleCallback();
    }
  }, [user]);

  const handleCallback = async () => {
    try {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) throw new Error(`Authorization failed: ${error}`);
      if (!code) throw new Error('No authorization code received');
      if (!user) throw new Error('User not authenticated');

      setMessage('Exchanging authorization code for tokens...');
      const tokens = await calendarService.exchangeCodeForTokens(code);

      setMessage('Saving calendar connection...');
      await calendarService.saveCalendarConnection(user.id, tokens);

      setMessage('Syncing your calendar...');
      const connection = await calendarService.getCalendarConnection(user.id);
      await calendarService.syncAvailability(user.id, connection);

      posthog.capture('calendar_connect', { provider: 'microsoft' });
      setStatus('success');
      setMessage('Calendar connected successfully!');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      console.error('Calendar callback error:', error);
      setStatus('error');
      setMessage(error.message || 'Failed to connect calendar');
      setTimeout(() => navigate('/dashboard'), 4000);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.iconWrap}>
          {status === 'processing' && <div className={styles.spinner} />}
          {status === 'success' && <CheckCircle className={styles.successIcon} size={64} />}
          {status === 'error'   && <XCircle   className={styles.errorIcon}   size={64} />}
          {status === 'default' && <AlertCircle size={64} />}
        </div>

        <h2 className={styles.title}>
          {status === 'processing' && 'Connecting Calendar...'}
          {status === 'success'    && 'Success!'}
          {status === 'error'      && 'Connection Failed'}
        </h2>

        <p className={styles.message}>{message}</p>

        <div className={styles.footer}>
          <Calendar size={16} aria-hidden="true" />
          <span>Google Calendar</span>
        </div>

        {status === 'error' && (
          <div className={styles.errorBox}>
            <p><strong>Troubleshooting:</strong></p>
            <ul>
              <li>Make sure you granted calendar permissions</li>
              <li>Check that your Google account is active</li>
              <li>Try disconnecting and reconnecting your calendar</li>
            </ul>
          </div>
        )}

        {status === 'processing' && (
          <div className={styles.dots}>
            <div className={styles.dot} />
            <div className={styles.dot} />
            <div className={styles.dot} />
          </div>
        )}

        {status !== 'processing' && (
          <p className={styles.redirect}>Redirecting to dashboard...</p>
        )}
      </div>
    </div>
  );
}
